import { Field, Formik } from 'formik';
import React from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import LabeledInput from '../../../mdc/LabeledInput';
import * as RDBCase from '../../../services/buildfire/rdb/cases';
import TextArea from '../../common/TextArea';
import { uppercaseFirstLetter } from '../../../util/string';
import Form from '../../common/Form';
import Typography from '../../common/Typography';
import { IonButton } from '@ionic/react';
import HelperText from '../../../mdc/HelperText';
import { useFirebase } from '../../../services/firebase';
import { uploadSites } from '../../../services/firebase/storage';
import { getStorage } from 'firebase/storage';
import ImageStatusGrid from './ImageStatusGrid';
import UploadErrorSummary from './UploadErrorSummary';
import SnackbarContext from '../../../context/SnackbarContext';

export interface SiteFormSubmitButtonProps {
  onClick: () => void;
  disabled: boolean;
}
export interface SiteFormProps {
  initialValue?: RDBCase.Site;
  onSubmit: (
    site: RDBCase.Site,
    resetForm?: () => void,
    setFieldValue?: (field: string, value: any) => void
  ) => void;
  maxImages?: number;
  Form?: React.ComponentType<{}>;
  renderSubmitButton?: (props: SiteFormSubmitButtonProps) => React.ReactNode;
  promptMessage?: string;
  onFormChange?: (dirty: boolean) => void;
}

const StyledForm = styled(Form)`
  margin: 24px 0;
`;

const UploadWrapper = styled.div`
  margin-top: 24px;

  .mdc-text-field-helper-text {
    margin: 8px 0;
    padding: 0 16px;
  }
`;

const UploadMeta = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  margin: 12px 0;
`;

const FileCount = styled.span<{ $overLimit: boolean }>`
  font-size: 12px;
  font-weight: 600;
  color: ${(props) =>
    props.$overLimit
      ? 'var(--mdc-theme-error)'
      : 'var(--mdc-theme-text-secondary-on-background)'};
`;

const AlertText = styled(Typography)`
  color: var(--mdc-theme-secondary) !important;
  font-size: 12px;
  font-weight: bold;
  margin-bottom: 4px;
`;

const Label: React.FC<{}> = ({ children }) => (
  <Typography margin="8px 16px -4px 16px" fontSize="12px">
    {children}
  </Typography>
);

const buildInitialValues = (site?: RDBCase.Site) => ({
  location: site?.location || '',
  description: site?.description || '',
  additionalInfo: site?.additionalInfo || '',
  images: (site?.images || []) as (string | File | RDBCase.ImageStatus)[],
});

const SiteForm: React.FC<SiteFormProps> = ({
  initialValue,
  onSubmit,
  maxImages = 20,
  renderSubmitButton,
  Form,
  promptMessage,
  onFormChange,
}) => {
  const { currentUser, app } = useFirebase();
  const { setMessage } = React.useContext(SnackbarContext);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [isRetrying, setIsRetrying] = React.useState(false);
  const [retryingIndices, setRetryingIndices] = React.useState<Set<number>>(
    new Set()
  );
  const setFieldValueRef = React.useRef<
    ((field: string, value: any) => void) | null
  >(null);
  const initialFormValues = React.useMemo(
    () => buildInitialValues(initialValue),
    [initialValue]
  );

  const _renderSubmitButton =
    renderSubmitButton ||
    (({ onClick, disabled }: SiteFormSubmitButtonProps) => (
      <IonButton expand="full" onClick={onClick} disabled={disabled}>
        Done
      </IonButton>
    ));
  const FormComponent = Form || StyledForm;

  const handleRetry = async (
    images: (File | RDBCase.ImageStatus | string)[],
    failedIndices: number[]
  ) => {
    setIsRetrying(true);
    setRetryingIndices(
      (prev) => new Set([...Array.from(prev), ...failedIndices])
    );
    try {
      const storage = getStorage(app);
      const failedImages = failedIndices.map((i) => images[i]);
      const sitesToUpload: RDBCase.Site[] = [
        {
          location: 'temp',
          description: 'temp',
          images: failedImages,
        },
      ];

      const result = await uploadSites(
        storage,
        sitesToUpload,
        currentUser!.uid
      );

      const updatedImages = [...images];
      failedIndices.forEach((index, i) => {
        updatedImages[index] = result.sites[0].images[i];
      });

      setFieldValueRef.current?.('images', updatedImages);

      if (result.hasFailures) {
        setMessage({
          text: `${result.failedCount} image(s) failed to upload. Please try again.`,
        });
      }
    } catch (error) {
      console.error('Error retrying uploads:', error);
      setMessage({
        text: 'Failed to upload images. Please try again.',
      });
    } finally {
      setIsRetrying(false);
      setRetryingIndices((prev) => {
        const next = new Set(prev);
        failedIndices.forEach((i) => next.delete(i));
        return next;
      });
    }
  };

  return (
    <Formik
      validateOnMount
      initialValues={initialFormValues}
      onSubmit={async (values, formikBag) => {
        await onSubmit(
          values as RDBCase.Site,
          formikBag.resetForm,
          formikBag.setFieldValue
        );
      }}
      validationSchema={Yup.object({
        location: Yup.string().max(100).required('Required'),
        description: Yup.string().max(1000).required('Required'),
        additionalInfo: Yup.string().max(4000).optional(),
        images: Yup.array()
          .min(1)
          .max(maxImages, `Must have ${maxImages} or fewer images`)
          .required('Required'),
      })}
    >
      {({
        setFieldValue,
        setFieldTouched,
        values,
        submitForm,
        isValid,
        dirty,
        touched,
        errors,
        isSubmitting,
      }) => {
        setFieldValueRef.current = setFieldValue;

        // Notify parent when form becomes dirty
        React.useEffect(() => {
          onFormChange?.(dirty);
        }, [dirty]);

        const failedImages = values.images.filter(
          (img): img is RDBCase.ImageStatus =>
            typeof img === 'object' &&
            'status' in img &&
            img.status === 'failed'
        );

        const handleAddImages = () => {
          fileInputRef.current?.click();
        };

        const handleFilesSelected = (
          e: React.ChangeEvent<HTMLInputElement>
        ) => {
          const files = Array.from(e.target.files || []);
          if (files.length > 0) {
            setFieldValue('images', [...values.images, ...files]);
            setFieldTouched('images', true, false);
          }
          // Reset input so same file can be selected again
          e.target.value = '';
        };

        const imageCount = values.images.length;
        const imagesError =
          typeof errors.images === 'string' ? errors.images : '';
        const showImagesError =
          !!imagesError && (touched.images || imageCount > maxImages);

        return (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,video/*"
              multiple
              style={{ display: 'none' }}
              onChange={handleFilesSelected}
            />
            <FormComponent>
              <Label>Location</Label>
              <Field
                as={LabeledInput}
                name="location"
                tabIndex={0}
                error={
                  touched.location &&
                  uppercaseFirstLetter(errors.location as string)
                }
                maxLength={100}
              />
              <Label>Description</Label>
              <Field
                as={LabeledInput}
                name="description"
                error={
                  touched.description &&
                  uppercaseFirstLetter(errors.description as string)
                }
                maxLength={1000}
              />
              <Label>
                Do you have any specific questions or concerns for the
                pathologist?
              </Label>
              <Field
                as={TextArea}
                name="additionalInfo"
                label=""
                helpText="Optional"
                error={
                  touched.additionalInfo &&
                  uppercaseFirstLetter(errors.additionalInfo as string)
                }
                maxLength={4000}
              />
              <UploadWrapper>
                <AlertText>
                  Please submit 5+ photos of unique fields. Include photos at
                  10x, 40x, and 100x.
                </AlertText>
                <UploadMeta>
                  <Typography as="span" fontSize="12px">
                    Attachments
                  </Typography>
                  <FileCount $overLimit={imageCount > maxImages}>
                    {imageCount}/{maxImages}
                  </FileCount>
                </UploadMeta>

                {failedImages.length > 0 && (
                  <UploadErrorSummary
                    failedCount={failedImages.length}
                    errors={
                      failedImages
                        .map((img) => img.error)
                        .filter(Boolean) as string[]
                    }
                    onRetryAll={() => {
                      const failedIndices = values.images
                        .map((img, i) =>
                          typeof img === 'object' &&
                          'status' in img &&
                          img.status === 'failed'
                            ? i
                            : -1
                        )
                        .filter((i) => i !== -1);
                      handleRetry(values.images, failedIndices);
                    }}
                    disabled={isRetrying}
                  />
                )}

                <ImageStatusGrid
                  images={values.images}
                  onRemove={(index) => {
                    const newImages = values.images.filter(
                      (_, i) => i !== index
                    );
                    setFieldValue('images', newImages);
                    setFieldTouched('images', true, false);
                  }}
                  onRetry={(index) => {
                    handleRetry(values.images, [index]);
                  }}
                  onAddImages={handleAddImages}
                  userId={currentUser?.uid}
                  getDisabled={(index) => retryingIndices.has(index)}
                  maxImages={maxImages}
                />

                <HelperText>
                  Attachments will be uploaded when you proceed to payment.
                </HelperText>
                {showImagesError && (
                  <HelperText style={{ color: 'var(--mdc-theme-error)' }}>
                    {imagesError}
                  </HelperText>
                )}
              </UploadWrapper>
            </FormComponent>

            {_renderSubmitButton({
              onClick: submitForm,
              disabled: !isValid || isSubmitting || isRetrying,
            })}
          </>
        );
      }}
    </Formik>
  );
};

export default SiteForm;
